/**
 * Morph · Morph+ 솔루션 상세 페이지 · 영상 분석(T1 MRI 뇌 위축 정량)
 * 내용은 제품팀 소개 페이지(2026-10-23)를 옮긴 것. 그래픽 수치는 예시.
 */
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import {
  CardGrid,
  CompareCards,
  DemoCta,
  DotGrid,
  DeviceInfo,
  FaqSection,
  FeatureRow,
  Figure,
  GaugeStat,
  HeroVisual,
  IntegrationSection,
  OvLabel,
  OvValue,
  PercentileLegend,
  Pill,
  PercentileRows,
  ProdSection,
  SampleNote,
  ScaleAxis,
  TrendChart,
} from '../../components/product-page.js';
import { DEMO_HREF, DEVICE_DISCLAIMER } from '../../lib/product-content.js';

function Page() {
  return (
    <PublicLayout disclaimerItems={DEVICE_DISCLAIMER}>
      <SolutionLayout
        stage="img"
        badgeLabel="IMG · 영상 분석"
        certs={['식약처 신고 D제신 25-34호', 'FDA 510(k) K260032 (Morph)']}
        domain="뇌 위축 정량 · 2D · 3D T1 MRI"
        name="Morph · Morph+"
        tagline="진료용 MRI로 보는 뇌 위축"
        description={<><strong>2D · 3D T1 MRI</strong>로 뇌 부피를 정량해 정상군 백분위와 뇌 연령으로 보여줍니다. Morph+는 알츠하이머형 뇌 패턴 · Aβ 양성 유사도를 더합니다.</>}
        cta={{ label: '데모 신청', href: DEMO_HREF }}
        visual={
          <HeroVisual
            title="Morph · Viewer"
            src="/solutions/morph-hero.png"
            alt="Morph Viewer 영역 분할 화면"
            caption="Viewer · 영역 분할 · 샘플 데이터"
            bl={
              <>
                <OvLabel>뇌 부피 · 정상군 100명 중</OvLabel>
                <div className="prod-ov-row">
                  <OvValue value="91" unit="번째" />
                  <Pill tone="warn">주의</Pill>
                </div>
                <DotGrid
                  sequence={[
                    { tone: 'ok', count: 24 },
                    { tone: 'warn', count: 6 },
                    { tone: 'me', count: 1 },
                    { tone: 'warn', count: 6 },
                    { tone: 'alert', count: 3 },
                  ]}
                />
              </>
            }
            tr={
              <>
                <OvLabel>뇌 연령</OvLabel>
                <OvValue value="78.3세" />
                <span className="prod-ov-note ok">실제 나이 77세보다 +1.3세</span>
              </>
            }
          />
        }
        crossSell={[
          { href: '/solutions/amylo', name: 'Amylo', desc: '아밀로이드 PET를 표준 단위 Centiloid로 정량', stage: 'img' },
          { href: '/solutions/morph-ct', name: 'Morph CT · CT+', desc: 'MRI 없이 CT로 보는 뇌 위축', stage: 'img' },
          { href: '/solutions/scst', name: 'SCST', desc: '인지 영역별 표준화 점수', stage: 'dx' },
        ]}
        after={
          <>
            <DemoCta name="Morph" />
            <DeviceInfo
              keys={['제품명', '모델명', '제품코드(등급)', '인허가', '기타', '제조원']}
              columns={[
                {
                  name: 'Morph',
                  rows: {
                    제품명: 'Morph',
                    모델명: 'BBM',
                    '제품코드(등급)': 'F1ABXA1 (1등급)',
                    인허가: '식약처 디지털의료기기 제조 신고 D제신 25-34호',
                    기타: 'FDA 510(k) K260032',
                    제조원: '(주)뷰브레인헬스케어',
                  },
                },
                {
                  name: 'Morph+',
                  rows: {
                    제품명: 'Morph Plus',
                    모델명: 'BBMP',
                    '제품코드(등급)': 'D1ABXA1 (1등급)',
                    인허가: '식약처 디지털의료기기 제조 신고 D제신 26-54호',
                    제조원: '(주)뷰브레인헬스케어',
                  },
                },
              ]}
            />
          </>
        }
      >
        {/* ── 개요 ─────────────────────────────────────────────── */}
        <ProdSection label="Overview" title="뇌 위축을 정상군과 비교해 수치로 보여줍니다">
          <CardGrid
            cols={3}
            items={[
              { title: '루틴 2D MRI 그대로', desc: '진료용 2D · 3D T1 MRI를 같은 기준으로 분석합니다.' },
              { title: '정상군 대비 위축 정도', desc: '연령 · 성별 정상군 백분위로 부위별 위축을 보여줍니다.' },
              { title: '뇌 연령', desc: '뇌 영상으로 추정한 나이를 실제 나이와 비교합니다.' },
            ]}
          />
        </ProdSection>

        {/* ── 주요 기능 ─────────────────────────────────────────── */}
        <ProdSection alt label="Features" title="주요 기능">
          <FeatureRow
            no="01"
            title="부위별 뇌 부피 · 정상군 백분위"
            desc="MRI에서 뇌 부위를 자동으로 나눠 좌 · 우 부피를 산출하고, 연령 · 성별 정상군 대비 위치로 보여줍니다."
            tags={['자동 분할', '좌 · 우 비교', '정상군 백분위']}
          >
            <Figure head="부위별 정상군 백분위 순서" legend={<PercentileLegend />}>
              <PercentileRows
                rows={[
                  { name: '전두엽', sub: '판단 · 계획 · 집중', value: 38, right: 30, left: 47, tone: 'ok', status: '정상 범위' },
                  { name: '측두엽', sub: '말 이해 · 기억 저장', value: 48, right: 40, left: 57, tone: 'ok', status: '정상 범위' },
                  { name: '두정엽', sub: '감각 · 방향 감각', value: 62, right: 65, left: 58, tone: 'ok', status: '정상 범위' },
                  { name: '후두엽', sub: '시각 처리', value: 78, right: 50, left: 91, tone: 'ok', status: '정상 범위' },
                  { name: '해마 인접', sub: '기억 관련', value: 100, right: 100, left: 90, tone: 'alert', status: '위축 뚜렷' },
                  { name: '뇌실 앞쪽', sub: '뇌가 줄면 커짐', value: 100, right: 100, left: 100, tone: 'alert', status: '위축 뚜렷' },
                  { name: '뇌실 뒤쪽', sub: '뇌가 줄면 커짐', value: 90, right: 99, left: 65, tone: 'warn', status: '주의' },
                ]}
              />
            </Figure>
          </FeatureRow>

          <FeatureRow
            no="02"
            reverse
            title="뇌 연령"
            desc="뇌 영상으로 추정한 나이를 실제 나이와 비교하고, 이전 검사 대비 변화를 보여줍니다."
            tags={['실제 나이 대비', '변화 속도']}
          >
            <Figure>
              <div className="prod-kpi">
                <div>
                  <div className="prod-stat-label">뇌 연령</div>
                  <div className="prod-kpi-value">
                    <OvValue value="78.3세" xl />
                    <span className="prod-kpi-delta ok">실제보다 +1.3세</span>
                  </div>
                </div>
                <Pill tone="ok">정상 범위</Pill>
              </div>
              <ScaleAxis
                min={40}
                max={90}
                ticks={['40세', '50', '60', '70', '80', '90세']}
                markers={[
                  { value: 77, label: '실제 77', kind: 'ref' },
                  { value: 78.3, label: '뇌 78.3', kind: 'value' },
                ]}
                rangeTone="ok"
              />
              <TrendChart
                points={[
                  { x: '이전 검사', y: 76, label: '76세' },
                  { x: '이번 검사', y: 78.3, label: '78.3세' },
                ]}
                refLine={[75, 77]}
                yMin={70}
                yMax={85}
                ticks={[70, 77.5, 85]}
                legend={[
                  { kind: 'ref', label: '실제 나이' },
                  { kind: 'series', label: '뇌 연령' },
                ]}
              />
            </Figure>
          </FeatureRow>

          <FeatureRow
            no="03"
            badge="Morph+"
            title="알츠하이머형 뇌 패턴 · Aβ 양성 유사도"
            desc="MRI 위축 패턴에 성별 · 나이 · APOE 정보를 더해 두 유사도를 0~100으로 제공합니다. 확진 검사가 아닌 참고 값입니다."
            tags={['알츠하이머형 뇌 패턴 유사도', 'Aβ 양성 유사도']}
          >
            <Figure>
              <div className="prod-grid-2">
                <GaugeStat title="알츠하이머형 뇌 패턴 유사도" value="41" unit="/ 100" gauge={41} thresholds={[50]} tone="ok" status="저위험" />
                <GaugeStat title="Aβ 양성 유사도" value="46" unit="/ 100" gauge={46} thresholds={[36, 72]} tone="warn" status="중위험" />
              </div>
              <p className="prod-figure-note">
                알츠하이머형 뇌 패턴: 50 이하 저 · 50 초과 고 / Aβ 양성: 36 이하 저 · 36~72 중 · 72 초과 고
                <br />
                확진 검사가 아닌 참고 값입니다.
              </p>
            </Figure>
          </FeatureRow>
          <SampleNote />
        </ProdSection>

        {/* ── 활용 환경 ─────────────────────────────────────────── */}
        <ProdSection label="Use Cases" title="이런 환경에서 활용합니다">
          <CardGrid
            cols={3}
            items={[
              { kicker: '진료', title: '신경과 외래', desc: '루틴 MRI로 뇌 위축 정도를 수치로 확인합니다.' },
              { kicker: '검진', title: '건강검진센터', desc: '환자 설명용 결과지로 검진 결과를 안내합니다.' },
              { kicker: '추적 관리', title: '인지저하 추적 관리', desc: '이전 검사 대비 위축 · 뇌 연령 변화를 확인합니다.' },
            ]}
          />
        </ProdSection>

        {/* ── Morph · Morph+ 비교 ───────────────────────────────── */}
        <ProdSection alt label="Morph · Morph+" title="필요에 맞게 Morph 또는 Morph+">
          <CompareCards
            items={[
              { name: 'Morph', input: 'T1 MRI', output: '뇌 부피 · 정상군 백분위 · 뇌 연령', foot: '위축 정량' },
              { name: 'Morph+', input: 'T1 MRI + 성별 · 나이 · APOE', output: '+ 알츠하이머형 뇌 패턴 · Aβ 양성 유사도', foot: '위축 정량 + 유사도' },
            ]}
          />
        </ProdSection>

        <IntegrationSection />

        <FaqSection
          alt
          items={[
            {
              q: '설치형과 클라우드 중 선택할 수 있나요?',
              a: '네. 병원 내 서버에 설치하는 방식과 클라우드 방식을 같은 구조로 제공하며, 기존 PACS와 연동할 수 있습니다.',
            },
            {
              q: '정상군 백분위는 무엇을 뜻하나요?',
              a: '같은 나이 · 성별 정상 참조군 안에서의 상대적 위치입니다. 결과지에는 환자가 이해하기 쉽게 100명 중 순서로 표시하며, 질병 단계를 뜻하지 않습니다.',
            },
            {
              q: 'Morph와 Morph+는 어떻게 다른가요?',
              a: 'Morph는 뇌 부피와 뇌 연령을 정량합니다. Morph+는 여기에 성별 · 나이 · APOE 정보를 더해 알츠하이머형 뇌 패턴 유사도와 Aβ 양성 유사도를 함께 제공합니다.',
            },
            {
              q: '분석 결과만으로 진단할 수 있나요?',
              a: '진단이나 치료를 단독으로 결정하는 용도가 아닙니다. Aβ 양성 유사도도 확진 검사가 아니며, 최종 판단은 담당의 종합 판단에 따릅니다.',
            },
          ]}
        />
      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/morph')({
  component: Page,
});
